import React, { ChangeEvent, useCallback, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import ScrapeApi from '../../../../api/ScrapeApi'
import { intersection, isMFNLink } from './utils'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import { RegisteredTextField } from '../../../../components/fields/TextField'
import { SourceTypeSelect } from './AdminSourceTypeSelect'
import { RegisteredDateTimePicker } from '../../../../components/fields/DateTimePicker'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import { IssueEntity } from 'types/IssueEntity'
import { WarrantEntity } from 'types/WarrantEntity'
import { SourceEntity } from 'types/SourceEntity'
import {
    singularConvertibleSourceTypes,
    singularIssueSourceTypes,
    singularWarrantSourceTypes,
} from '../../../../utils/issues/sourceTypes'
import { SourceType } from 'types/SourceType'
import { ConvertibleEntity } from 'types/ConvertibleEntity'

const getMinDate = (
    sourceType: SourceType,
    publicationDate: string | Date | null,
    sources: SourceEntity[],
    issue?: IssueEntity,
) => {
    if (!issue) return publicationDate

    if (sourceType === 'Terms') {
        return publicationDate
    }

    const termsSource = sources.find((s) => s.sourceType === 'Terms')

    if (sourceType === 'Outcome' && termsSource?.publishedDate) {
        return new Date(termsSource.publishedDate)
    }

    return publicationDate
}

const getMaxDate = (sourceType: SourceType, sources: SourceEntity[], issue?: IssueEntity) => {
    if (!issue) return null

    if (sourceType === 'Outcome') return null

    const outcomeSource = sources.find((s) => s.sourceType === 'Outcome')
    if (sourceType === 'Terms') {
        if (outcomeSource?.publishedDate) {
            return new Date(outcomeSource.publishedDate)
        }
        return null
    }

    if (sourceType === 'Publication') {
        const termsSource = sources.find((s) => s.sourceType === 'Terms')

        if (termsSource?.publishedDate) {
            return new Date(termsSource.publishedDate)
        }

        if (outcomeSource?.publishedDate) {
            return new Date(outcomeSource.publishedDate)
        }
    }

    return null
}

export type SourceForm = {
    sourceUrl: string
    internalComment: string
    sourceType: SourceType | ''
    title: string
    publishedDate: Date | null
}

export function SourceFormRow({
    onSubmit,
    issue,
    warrant,
    convertible,
    sources,
}: {
    onSubmit: (val: SourceForm) => void
    issue?: IssueEntity
    warrant?: WarrantEntity
    convertible?: ConvertibleEntity
    sources: SourceEntity[]
}) {
    const defaultValues: SourceForm = {
        sourceUrl: '',
        internalComment: '',
        sourceType: '',
        title: '',
        publishedDate: null,
    }
    const formMethods = useForm<SourceForm>({ defaultValues, mode: 'onBlur' })
    const { setValue } = formMethods
    const [scraping, setScraping] = useState(false)
    const entity = issue || warrant || convertible
    const sourceType = formMethods.watch('sourceType')

    const publicationSource = sources.find((s) => s.sourceType === 'Publication')
    let publicationDate: string | Date | null =
        publicationSource?.publishedDate ||
        issue?.publicationDate ||
        warrant?.publicationDate ||
        convertible?.publicationDate ||
        null
    if (publicationDate) publicationDate = new Date(publicationDate)

    const minDate = getMinDate(sourceType as SourceType, publicationDate, sources, issue)
    const maxDate = getMaxDate(sourceType as SourceType, sources, issue)

    // Scrape data and update sourceUrl in the form state
    const handleSourceUrlChange = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            const scrapeApi = new ScrapeApi()
            const url = e.target.value
            setValue('sourceUrl', url) // Update sourceUrl value in form state

            if (!isMFNLink(url)) return // Only trigger for MFN links
            setScraping(true)
            try {
                const data = await scrapeApi.read(url)
                if (data.title) {
                    setValue('title', data.title)
                }
                if (data.date) {
                    setValue('publishedDate', new Date(data.date))
                }
            } catch (error) {
                console.error('Failed to scrape data:', error)
            } finally {
                setScraping(false)
            }
        },
        [setValue],
    )

    const handleSubmit = () => {
        onSubmit(formMethods.getValues())
        formMethods.reset(defaultValues)
    }

    if (!entity) {
        return <div>SourceFormRow: Missing entity.</div>
    }

    const issueType = issue?.type || (warrant ? 'warrant' : convertible ? 'convertible' : null)
    const registeredSourceTypes: SourceType[] = entity.sources?.map((source) => source.sourceType)
    const singularSourceTypes = issue
        ? singularIssueSourceTypes
        : warrant
          ? singularWarrantSourceTypes
          : singularConvertibleSourceTypes

    return (
        <FormProvider {...formMethods}>
            <TableRow key='sourceForm'>
                <TableCell>
                    <RegisteredTextField
                        label='URL'
                        name='sourceUrl'
                        onChange={handleSourceUrlChange} // Trigger scrape on change
                        validation={{
                            validate: (url: string) => {
                                const sourceAlreadyExists = sources.some((s) => s.sourceUrl === url)
                                return !sourceAlreadyExists || 'Källan är redan tillagd'
                            },
                        }}
                    />
                </TableCell>
                <TableCell>
                    <RegisteredTextField label='Intern kommentar' name='internalComment' />
                </TableCell>
                <TableCell>
                    <SourceTypeSelect
                        label='Typ'
                        name='sourceType'
                        formMethods={formMethods}
                        issueType={issueType}
                        excludeSourceTypes={intersection(singularSourceTypes, registeredSourceTypes)}
                        validation={{ required: 'Typ måste anges' }}
                    />
                </TableCell>
                <TableCell>
                    <RegisteredTextField label='Titel' name='title' validation={{ required: 'Titel måste anges' }} />
                </TableCell>
                <TableCell>
                    <RegisteredDateTimePicker
                        label='Publicerad'
                        name='publishedDate'
                        minDate={minDate}
                        maxDate={maxDate}
                        validation={{ required: 'Datum måste anges' }}
                    />
                </TableCell>
                <TableCell>
                    <Button
                        variant='outlined'
                        onClick={(...args) => void formMethods.handleSubmit(handleSubmit)(...args)}
                        disabled={scraping}
                    >
                        Lägg&nbsp;till
                    </Button>
                    {scraping && <Typography variant='caption'>Loading...</Typography>}
                </TableCell>
            </TableRow>
        </FormProvider>
    )
}
